import type React from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { AlertTriangle, ArrowLeft, Bug, Home, Phone, RefreshCw } from 'lucide-react'
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom'
import { ROUTES } from '@/config/routes'

export default function ErrorPage() {
  const [reportSubmitted, setReportSubmitted] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const error = useRouteError()

  const handleRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }

  const handleReportSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setReportSubmitted(true)
  }

  function extractError(error: unknown): string {
    console.error(error) // ainda mantém o dump no console devtools

    // 1. Erros HTTP lançados via Response()
    if (isRouteErrorResponse(error)) {
      const { status, statusText, data } = error
      const body = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
      return `HTTP ${status} ${statusText}\n\n${body}`
    }

    // 2. Erros JS normais
    if (error instanceof Error) {
      // mostra a stack completa (inclui mensagem + trace)
      return error.stack ?? error.message
    }

    // 3. Qualquer outro objeto (fallback genérico)
    try {
      return JSON.stringify(error, null, 2)
    } catch {
      return String(error)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto pt-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Algo deu errado!</h1>
          <p className="text-slate-600 text-lg">Parece que aconteceu um erro inespesperado no seu navegador!</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                Detalhes do erro
              </CardTitle>
              <CardDescription>
                <pre className="mt-2 max-h-60 overflow-auto rounded-md bg-slate-950 p-4 text-white">
                  {extractError(error)}
                </pre>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Data e hora:</strong> {new Date().toLocaleString()}
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Error ID:</strong> ERR-{Math.random().toString(36).substr(2, 9).toUpperCase()}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleRefresh} disabled={isRefreshing} className="flex-1">
                  <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Recarregando...' : 'Tentar novamente'}
                </Button>
                <Button variant="outline" asChild className="flex-1">
                  <Link to={ROUTES.HOME.path}>
                    <Home className="mr-2 h-4 w-4" />
                    Ir para Home
                  </Link>
                </Button>
              </div>

              <Button variant="ghost" asChild className="w-full">
                <Link to={ROUTES.PREVIOUS.path}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Precisa de ajuda?</CardTitle>
              <CardDescription>Entre em contato com nosso time de suporte</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <Phone className="mr-2 h-4 w-4" />
                  Ligue para: 0800-SUPPORT
                </Button>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium mb-3">Reporte o erro</h4>
                {!reportSubmitted ? (
                  <form onSubmit={handleReportSubmit} className="space-y-3">
                    <div>
                      <Label htmlFor="email" className="text-sm">
                        Email
                      </Label>
                      <Input id="email" type="email" placeholder="your@email.com" required />
                    </div>
                    <div>
                      <Label htmlFor="description" className="text-sm">
                        Descreva os passos que fez até ocorrer o erro
                      </Label>
                      <Textarea
                        id="description"
                        placeholder="Describe what happened..."
                        className="min-h-[80px]"
                        required
                      />
                    </div>
                    <Button type="submit" size="sm" className="w-full">
                      Enviar
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-4">
                    <div className="bg-green-50 text-green-700 p-3 rounded-lg">
                      <p className="text-sm font-medium">Enviado com sucesso!</p>
                      <p className="text-xs mt-1">Vamos trabalhar para resolver o seu problema o quanto antes.</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8 text-sm text-slate-500">
          <p className="mt-1">© {new Date().getFullYear()} Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  )
}
